<template>
  <panel class="case-grading" title="Grading Rubric" color="green-5" data-testid="grading-rubric">
    <template v-slot:title>
      <div v-if="isAiAvailable && isAiDialogueEnabled" class="ai-switch-container">
        <toggle-switch
          class="global-toggle md-primary ui-theme-3"
          :value="state.caseConf.grading && state.caseConf.grading.aiAssessmentEnabled"
          on-color="#7D30C9"
          :readonly="readonly || !isAiDialogueEnabled"
          @toggle="value => setAiEnabled(value)"
        />
        <div class="ai-description-container">
          <label>AI Dialogue Assessment</label>
          <span>
            Users will be presented with an AI driven assessment of their dialogue at the end of the scenario if enabled.
          </span>
        </div>
      </div>
    </template>
    <div class="accordion">
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('dialogue')}"
          @click="toggleActivePanel('dialogue')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Dialogue</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ dialoguePoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(dialoguePoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="dialogueIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('dialogue')" class="accordion-content">
            <template v-if="!isAiDialogueEnabled">
              <grading-item
                v-for="question in caseConf.dialogue.questions || []"
                :key="question.id"
                label="Question"
                :description="question.content.en"
                :grade="getGrade('questions', question.id)"
                :points="getPoints('questions', question.id)"
                :is-critical="getIsCritical('questions', question.id)"
                :readonly="readonly"
                @changeGrade="value => setGrade('questions', question.id, value)"
                @changePoints="value => setPoints('questions', question.id, value)"
                @changeIsCritical="value => setIsCritical('questions', question.id, value)"
              />
              <grading-item
                v-for="command in caseConf.dialogue.commands || []"
                :key="command.id"
                label="Command"
                :description="command.content.en"
                :grade="getGrade('commands', command.id)"
                :points="getPoints('commands', command.id)"
                :is-critical="getIsCritical('commands', command.id)"
                :readonly="readonly"
                @changeGrade="value => setGrade('commands', command.id, value)"
                @changePoints="value => setPoints('commands', command.id, value)"
                @changeIsCritical="value => setIsCritical('commands', command.id, value)"
              />
            </template>
            <fragment v-for="call in caseConf.dialogue.calls || []" :key="call.id">
              <grading-item
                v-for="item in call.items || []"
                :key="item.id"
                :label="`Call - ${call.callee.en}`"
                :description="item.content.en"
                :grade="getGrade('calls', call.id, 'item', item.id)"
                :points="getPoints('calls', call.id, 'item', item.id)"
                :is-critical="getIsCritical('calls', call.id, 'item', item.id)"
                :readonly="readonly"
                @changeGrade="value => setGrade('calls', call.id, value, 'item', item.id)"
                @changePoints="value => setPoints('calls', call.id, value, 'item', item.id)"
                @changeIsCritical="value => setIsCritical('calls', call.id, value, 'item', item.id)"
              />
            </fragment>
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('customMenu')}"
          @click="toggleActivePanel('customMenu')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Custom Menus</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ customMenuPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(customMenuPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="customMenuIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('customMenu')" class="accordion-content">
            <fragment v-for="customMenu in caseConf.customMenus || []" :key="customMenu.category">
              <fragment v-for="parent in customMenu.items || []" :key="parent.id">
                <grading-item
                  :label="`Custom Menu - ${customMenu.category}`"
                  :description="parent.content.en"
                  :grade="getGrade('customMenus', customMenu.category, parent.id, 'no-child')"
                  :points="getPoints('customMenus', customMenu.category, parent.id, 'no-child')"
                  :is-critical="getIsCritical('customMenus', customMenu.category, parent.id, 'no-child')"
                  :readonly="readonly"
                  @changeGrade="value => setGrade('customMenus',customMenu.category, value, parent.id, 'no-child')"
                  @changePoints="value => setPoints('customMenus', customMenu.category, value, parent.id, 'no-child')"
                  @changeIsCritical="value => setIsCritical('customMenus', customMenu.category, value, parent.id, 'no-child')"
                />
                <grading-item
                  v-for="child in parent.children"
                  :key="child.id"
                  :label="`Custom Menu - ${customMenu.category}`"
                  :description="`${parent.content.en} - ${child.content.en}`"
                  :grade="getGrade('customMenus', customMenu.category, parent.id, child.id)"
                  :points="getPoints('customMenus', customMenu.category, parent.id, child.id)"
                  :is-critical="getIsCritical('customMenus', customMenu.category, parent.id, child.id)"
                  :readonly="readonly"
                  @changeGrade="value => setGrade('customMenus', customMenu.category, value, parent.id, child.id)"
                  @changePoints="value => setPoints('customMenus', customMenu.category, value, parent.id, child.id)"
                  @changeIsCritical="value => setIsCritical('customMenus', customMenu.category, value, parent.id, child.id)"
                />
              </fragment>
            </fragment>
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('assessments')}"
          @click="toggleActivePanel('assessments')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Assessments</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ assessmentsPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(assessmentsPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="assessmentsIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('assessments')" class="accordion-content">
            <grading-item
              v-for="assessmentItem in assessmentGradingItems"
              :key="`${assessmentItem.id}-${assessmentItem.component}-${assessmentItem.componentKey}`"
              :label="assessmentItem.label"
              :description="assessmentItem.description"
              :grade="getGrade('assessments', assessmentItem.id, assessmentItem.component, assessmentItem.componentKey)"
              :points="getPoints('assessments', assessmentItem.id, assessmentItem.component, assessmentItem.componentKey)"
              :is-critical="getIsCritical('assessments', assessmentItem.id, assessmentItem.component, assessmentItem.componentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('assessments', assessmentItem.id, value, assessmentItem.component, assessmentItem.componentKey)"
              @changePoints="value => setPoints('assessments', assessmentItem.id, value, assessmentItem.component, assessmentItem.componentKey)"
              @changeIsCritical="value => setIsCritical('assessments', assessmentItem.id, value, assessmentItem.component, assessmentItem.componentKey)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('IV')}"
          @click="toggleActivePanel('IV')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>IV</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ ivPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(ivPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="ivIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('IV')" class="accordion-content">
            <grading-item
              v-for="ivItem in ivGradingItems"
              :key="`${ivItem.component}-${ivItem.componentKey}-${ivItem.subComponentKey}`"
              :label="ivItem.label"
              :description="ivItem.description"
              :grade="getGrade('treatments', 'IV', ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
              :points="getPoints('treatments', 'IV', ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
              :is-critical="getIsCritical('treatments', 'IV', ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('treatments', 'IV', value, ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
              @changePoints="value => setPoints('treatments', 'IV', value, ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
              @changeIsCritical="value => setIsCritical('treatments', 'IV', value, ivItem.component, ivItem.componentKey, ivItem.subComponentKey)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('MEDICATIONS')}"
          @click="toggleActivePanel('MEDICATIONS')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Meds/Fluids</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ medicationsPoints + fluidsPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage((medicationsPoints + fluidsPoints) / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="medicationsIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('MEDICATIONS')" class="accordion-content">
            <grading-item
              v-for="medicationItem in medicationGradingItems"
              :key="`${medicationItem.id}-${medicationItem.componentKey}-${medicationItem.subComponentKey}`"
              :label="medicationItem.label"
              :description="medicationItem.description"
              :grade="getGrade('medications', medicationItem.id, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
              :points="getPoints('medications', medicationItem.id, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
              :is-critical="getIsCritical('medications', medicationItem.id, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('medications', medicationItem.id, value, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
              @changePoints="value => setPoints('medications', medicationItem.id, value, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
              @changeIsCritical="value => setIsCritical('medications', medicationItem.id, value, medicationItem.component, medicationItem.componentKey, medicationItem.subComponentKey)"
            />
            <grading-item
              v-for="fluidItem in fluidGradingItems"
              :key="`${fluidItem.id}-${fluidItem.componentKey}-${fluidItem.subComponentKey}`"
              :label="fluidItem.label"
              :description="fluidItem.description"
              :grade="getGrade('fluids', fluidItem.id, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
              :points="getPoints('fluids', fluidItem.id, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
              :is-critical="getIsCritical('fluids', fluidItem.id, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('fluids', fluidItem.id, value, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
              @changePoints="value => setPoints('fluids', fluidItem.id, value, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
              @changeIsCritical="value => setIsCritical('fluids', fluidItem.id, value, fluidItem.component, fluidItem.componentKey, fluidItem.subComponentKey)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('OXYGEN')}"
          @click="toggleActivePanel('OXYGEN')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Oxygen</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ oxygenRoutesPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(oxygenRoutesPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="oxygenRoutesIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('OXYGEN')" class="accordion-content">
            <grading-item
              v-for="oxygenRoute in oxygenRoutes"
              :key="oxygenRoute.id"
              label="Oxygen"
              :description="oxygenName(oxygenRoute.id)"
              :grade="getGrade('oxygenRoutes', oxygenRoute.id)"
              :points="getPoints('oxygenRoutes', oxygenRoute.id)"
              :is-critical="getIsCritical('oxygenRoutes', oxygenRoute.id)"
              :readonly="readonly"
              @changeGrade="value => setGrade('oxygenRoutes', oxygenRoute.id, value)"
              @changePoints="value => setPoints('oxygenRoutes', oxygenRoute.id, value)"
              @changeIsCritical="value => setIsCritical('oxygenRoutes', oxygenRoute.id, value)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('PROCEDURES')}"
          @click="toggleActivePanel('PROCEDURES')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Procedures</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ proceduresPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(proceduresPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="proceduresIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('PROCEDURES')" class="accordion-content">
            <grading-item
              v-for="procedureItem in procedureGradingItems"
              :key="`${procedureItem.id}-${procedureItem.component}-${procedureItem.componentKey}`"
              :label="procedureItem.label"
              :description="procedureItem.description"
              :grade="getGrade('treatments', procedureItem.id, procedureItem.component, procedureItem.componentKey)"
              :points="getPoints('treatments', procedureItem.id, procedureItem.component, procedureItem.componentKey)"
              :is-critical="getIsCritical('treatments', procedureItem.id, procedureItem.component, procedureItem.componentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('treatments', procedureItem.id, value, procedureItem.component, procedureItem.componentKey)"
              @changePoints="value => setPoints('treatments', procedureItem.id, value, procedureItem.component, procedureItem.componentKey)"
              @changeIsCritical="value => setIsCritical('treatments', procedureItem.id, value, procedureItem.component, procedureItem.componentKey)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('MONITORS')}"
          @click="toggleActivePanel('MONITORS')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Monitors</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ monitorActionsPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(monitorActionsPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="monitorActionsIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('MONITORS')" class="accordion-content">
            <grading-item
              v-for="monitorItem in monitorActionsGradingItems"
              :key="`${monitorItem.id}-${monitorItem.component}-${monitorItem.componentKey}`"
              :label="monitorItem.label"
              :description="monitorItem.description"
              :grade="getGrade('treatments', monitorItem.id, monitorItem.component, monitorItem.componentKey)"
              :points="getPoints('treatments', monitorItem.id, monitorItem.component, monitorItem.componentKey)"
              :is-critical="getIsCritical('treatments', monitorItem.id, monitorItem.component, monitorItem.componentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('treatments', monitorItem.id, value, monitorItem.component, monitorItem.componentKey)"
              @changePoints="value => setPoints('treatments', monitorItem.id, value, monitorItem.component, monitorItem.componentKey)"
              @changeIsCritical="value => setIsCritical('treatments', monitorItem.id, value, monitorItem.component, monitorItem.componentKey)"
            />
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('CASE_LOGIC')}"
          @click="toggleActivePanel('CASE_LOGIC')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Scenario Logic</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ gradingEffectNodesPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(gradingEffectNodesPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="gradingEffectNodesIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('CASE_LOGIC')" class="accordion-content">
            <div
              v-for="gradingEffectNode in caseLogicGradingEffectNodes"
              :key="gradingEffectNode.id"
              class="grading-effect-node"
            >
              <grading-item
                :label="gradingEffectNode.data.effect.label"
                :grade="gradingEffectNode.data.effect.grade"
                :points="gradingEffectNode.data.effect.points"
                :is-critical="gradingEffectNode.data.effect.isCritical"
                :readonly="true"
                :disable-neutral="true"
              />
              <md-button @click="goToCaseLogicNode(gradingEffectNode)">Go To Node</md-button>
            </div>
          </div>
        </transition>
      </div>
      <div class="accordion-item">
        <md-button
          class="md-raised accordion-trigger"
          :class="{open: isActivePanel('INTERVENTIONS')}"
          @click="toggleActivePanel('INTERVENTIONS')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Interventions</label>
          <div class="rubric-statistics">
            <span class="rubric-statistic green-6 color border">
              {{ interventionPoints }}
              <md-tooltip md-direction="right">Point total for section</md-tooltip>
            </span>
            <span class="rubric-statistic green-6 color border">
              {{ formatPercentage(interventionPoints / totalPoints) }}
              <md-tooltip md-direction="right">Weight of total score</md-tooltip>
            </span>
            <md-icon v-if="interventionsIsCritical" class="rubric-critical red-3 color">
              warning
              <md-tooltip md-direction="right">If any critical actions are present</md-tooltip>
            </md-icon>
          </div>
        </md-button>
        <transition name="fold">
          <div v-show="isActivePanel('INTERVENTIONS')" class="accordion-content">
            <grading-item
              v-for="intvItem in interventionGradingItems"
              :key="`${intvItem.id}-${intvItem.component}-${intvItem.componentKey}-${intvItem.subComponentKey}`"
              :label="intvItem.label"
              :description="intvItem.description"
              :grade="getGrade('interventions', intvItem.id, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
              :points="getPoints('interventions', intvItem.id, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
              :is-critical="getIsCritical('interventions', intvItem.id, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
              :readonly="readonly"
              @changeGrade="value => setGrade('interventions', intvItem.id, value, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
              @changePoints="value => setPoints('interventions', intvItem.id, value, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
              @changeIsCritical="value => setIsCritical('interventions', intvItem.id, value, intvItem.component, intvItem.componentKey, intvItem.subComponentKey)"
            />
          </div>
        </transition>
      </div>
    </div>
    <div class="preference-assessment-row">
      <div class="output-preference">
        <label>Grading Output Preference</label>
        <md-field class="ui-theme-3">
          <md-select
            :value="outputPreference"
            :disabled="readonly"
            @input="setOutputPreference"
          >
            <md-option
              v-for="pref in outputPreferences"
              :key="pref.key"
              :value="pref.key"
            >
              {{ pref.label.en }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <html-editor
        v-if="isAiDialogueEnabled && isAiAssessmentEnabled"
        label="Standards"
        subtitle="The assessment is based upon the parameters written below. How should the AI grade dialogue?"
        :contents="promptMarkup"
        :readonly="readonly"
        :reload="state.reloadPrompt"
        :prevent-emojis="true"
        @update="editPrompt"
      />
    </div>
    <div
      v-if="isAiDialogueEnabled && isAiAssessmentEnabled"
      class="standards-row"
    >
      <assessment-prompt-dialog
        :case-conf="caseConf"
        :readonly="readonly"
        :prompt-contents="promptContent"
        title="Add New: Standard"
        button-label="Standards"
        @update="replacePrompt"
      />
    </div>
  </panel>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import flatten from 'lodash/flatten';
import flattenDeep from 'lodash/flattenDeep';
import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import toString from 'lodash/toString';
import get from 'lodash/get';
import toArray from 'lodash/toArray';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import sum from 'lodash/sum';
import GradingItem from '~lib/common/components/GradingItem';
import defaultRoutes from '~lib/common/config/routes';
import pulseLocations from '~lib/common/config/pulseLocations';
import vertebrae from '~lib/common/config/vertebrae';
import { interventions, categoryLabel } from '~lib/common/config/interventions';
import iv from '~lib/common/config/iv';
import physicalAssessmentLocations from '~lib/common/config/physicalAssessmentLocations';

import Panel from '../../common/Panel';
import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';
import { fluids } from '~lib/common/config/fluids';
import AssessmentPromptDialog from '../../common/PromptDialog/AssessmentPromptContainer';
import ToggleSwitch from '../../common/ToggleSwitch.vue';

const defaultGrading = {
  grade: 'NEUTRAL',
  points: 0,
  isCritical: false,
};

const outputPreferences = [
  { key: 'PASS_FAIL', label: { en: 'Success/Fail & Percentage' } },
  { key: 'ACTION_SCORES', label: { en: 'Action Scores' } },
  { key: 'FULL_DETAIL', label: { en: 'Full Action Breakdown' } },
];

export default {
  name: 'case-grading',
  components: {
    Panel,
    GradingItem,
    HtmlEditor,
    AssessmentPromptDialog,
    ToggleSwitch,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    ivFluids: {
      type: Array,
      default: () => [],
    },
    ivs: {
      type: Array,
      default: () => [],
    },
    medications: {
      type: Array,
      default: () => [],
    },
    oxygens: {
      type: Array,
      default: () => [],
    },
    treatments: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      state: {
        activeAccordionPanel: 'dialogue',
        caseConf: {
          grading: {
            questions: [],
            outputPreference: 'ACTION_SCORES',
          },
        },
        reloadPrompt: false,
      },
      outputPreferences,
    };
  },
  computed: {
    assessmentGradingItems() {
      const pulseItems = pulseLocations.map(location => ({
        id: 'PALPABLE_PULSE',
        component: 'PALPABLE_PULSE',
        componentKey: location.key,
        label: 'Palpate Pulse',
        description: location.label.en,
      }));
      const spineItems = (this.caseConf.spinalAssessments || []).map(assessment => {
        const vertebra = vertebrae.find(v => v.key === assessment.key);
        return {
          id: 'SPINAL_ASSESSMENT',
          component: 'SPINAL_ASSESSMENT',
          componentKey: assessment.key,
          label: 'Spinal Assessment',
          description: vertebra.label.en,
        };
      })
      return pulseItems.concat(spineItems);
    },
    treatmentsById() {
      return keyBy(this.treatments, 'id');
    },
    iv() {
      return this.ivs.find(t => t.id === 'IV');
    },
    caseIv() {
      const { _treatments } = this.caseConf;
      if (!_treatments) {
        return null;
      }
      return _treatments.find(t => t.id === 'IV');
    },
    ivFluidsById() {
      return keyBy(this.ivFluids, 'id');
    },
    ivSitesByKey() {
      return keyBy(this.iv.sites, 'key');
    },
    ivFluidRatesByKey() {
      return keyBy(this.iv.fluidRates, 'key');
    },
    ivGradingItems() {
      if (!this.caseConf.iv || !this.caseConf.iv.enabled) {
        return [];
      }
      const items = [];
      for (const ivType of this.caseConf.iv.types) {
        // Side/Site
        for (const ivSite of ivType.sites) {
          for (const ivSide of ivSite.sides) {
            const side = iv.sides.find(side => side.key === ivSide);
            const site = iv.sites.find(site => site.key === ivSite.site);
            const sideStr = side ? side.label.en : 'UNKNOWN';
            const siteStr = site ? site.label.en : 'UNKNOWN';
            items.push({
              label: 'IV Site',
              description: `${sideStr} ${siteStr}`,
              component: 'site',
              componentKey: ivSite.site,
              subComponentKey: ivSide,
            });
          }
        }
        // Needle gauges and needle sets
        for (const ivGauge of ivType.gauges) {
          if (ivType.type === 'PERIPHERAL') {
            const gauge = iv.needleGauges.find(ng => ng.key === ivGauge);
            items.push({
              label: 'IV Needle Gauge',
              description: gauge ? gauge.label.en : 'UNKNOWN',
              component: 'needleGauge',
              componentKey: ivGauge,
            });
          }
          if (ivType.type === 'INTRAOSSEOUS') {
            const set = iv.needleSets.find(ns => ns.key === ivGauge);
            items.push({
              label: 'IV Needle Set',
              description: set ? set.label.en : 'UNKNOWN',
              component: 'needleSet',
              componentKey: ivGauge,
            });
          }
        }
        // Saline lock
        if (ivType.salineLock && !items.find(item => item.component === 'salineLock')) {
          items.push({
            label: 'IV Saline Lock',
            description: 'IV Saline Lock',
            component: 'salineLock',
            componentKey: true,
          });
        }
      }
      return items;
    },
    medicationsById() {
      return keyBy(this.medications.concat(this.caseConf.customMedications || []), 'id');
    },
    medicationRoutesByKey() {
      return keyBy(defaultRoutes, 'key');
    },
    medicationGradingItems() {
      const { dosageForms } = this.caseConf;
      if (!dosageForms) {
        return [];
      }
      return flattenDeep(
        dosageForms
          .filter(dosageForm => this.medicationsById[dosageForm.medicationId])
          .map(dosageForm =>{
            const medication = this.medicationsById[dosageForm.medicationId];
            return dosageForm.enabledRoutes
              .filter(route => this.medicationRoutesByKey[route.key])
              .map(route => {
                const gradingItem = {
                  id: medication.id,
                  label: `(Medication) ${this.medicationsById[medication.id].name.en}`,
                  description: this.medicationRoutesByKey[route.key].name.en,
                  component: 'route',
                  componentKey: route.key,
                };
                return gradingItem;
              });
          }),
      );
    },
    fluidGradingItems() {
      const caseFluids = this.caseConf.fluids || [];
      return caseFluids.map(caseFluid => {
        const fluid = (fluids.concat(this.caseConf.customFluids || [])).find(fluid => fluid.key === caseFluid.key);
        return ({
          id: caseFluid.key,
          label: `(Fluid) ${fluid.label.en}`,
          description: 'IV Infusion',
      })});
    },
    oxygenRoutes() {
      return (this.caseConf._oxygens || []).filter(o =>
        get(o, 'options.enabled'),
      );
    },
    procedureGradingItems() {
      const { _treatments } = this.caseConf;
      if (!_treatments) {
        return [];
      }
      return flatten(
        _treatments
          .map(t => ({ ...this.treatmentsById[t.id], options: t.options }))
          .filter(t => t.category === 'PROCEDURE')
          .filter(caseProcedure => get(caseProcedure, 'options.enabled'))
          .concat(this.treatments.filter(t => t.category === 'PROCEDURE' &&
            (t.id === 'RAISE_BED_RAIL' || t.id === 'LOWER_BED_RAIL')))
          .filter(caseProcedure => {
            // Bed rail procedures are only enabled in certain scenes
            if (caseProcedure.id === 'RAISE_BED_RAIL' || caseProcedure.id === 'LOWER_BED_RAIL') {
              const bedRailEnabledScenes = ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'];
              const caseHasBedRail =
                bedRailEnabledScenes.includes(this.caseConf._startLocation.key) ||
                (this.caseConf._endLocation &&
                  bedRailEnabledScenes.includes(this.caseConf._endLocation.key))
              return caseHasBedRail;
            }
            if (caseProcedure.id == 'UPPER_UNDERWEAR_REMOVED' &&
              !get(this.caseConf._avatar, 'avatarOptions.upperUnderwear', false)) {
              return false;
            }
            return true;
          })
          .map(caseProcedure => {
            const procedure = this.treatmentsById[caseProcedure.id];
            if (caseProcedure.id === 'PERSONAL_PROTECTIVE_EQUIPMENT') {
              const componentOptions =
                caseProcedure.options && caseProcedure.options.componentOptions;
              return procedure.components
                .filter(
                  component =>
                    componentOptions &&
                    componentOptions[component.key] &&
                    componentOptions[component.key].toggled,
                )
                .map(component => ({
                  id: caseProcedure.id,
                  label: 'Procedures',
                  description: `${procedure.name.en} - ${component.label.en}`,
                  component: component.key,
                  componentKey: true,
                }));
            }
            return [
              {
                id: caseProcedure.id,
                label: 'Procedures',
                description: procedure.name.en,
              },
            ];
          }),
      )
    },
    monitorActionsGradingItems() {
      const { _treatments } = this.caseConf;
      if (!_treatments) {
        return [];
      }
      const monitorProcedures = _treatments
        .map(t => ({ ...this.treatmentsById[t.id], options: t.options }))
        .filter(t => t.category === 'MONITOR_PROCEDURE')
        .filter(caseProcedure => get(caseProcedure, 'options.enabled'));
      return flatten(
        monitorProcedures.map(monitorProcedure => {
          const caseMonitorProcedure = _treatments.find(
            t => t.id === monitorProcedure.id,
          );
          const componentOptions = get(
            caseMonitorProcedure,
            'options.componentOptions',
          );
          if (monitorProcedure.id === 'LEAD_SELECT') {
            const leadComponent = monitorProcedure.components.find(
              component => component.key === 'LEAD',
            );
            if (!leadComponent) {
              return [];
            }
            return leadComponent.metadata.values
              .filter(value =>
                get(componentOptions, 'LEAD.availableValues', []).includes(
                  value.key,
                ),
              )
              .map(value => ({
                id: monitorProcedure.id,
                label: monitorProcedure.name.en,
                description: value.label.en,
                component: 'LEAD',
                componentKey: value.key,
              }));
          }
          return [
            {
              id: monitorProcedure.id,
              label: 'Monitor',
              description: monitorProcedure.name.en,
            },
          ];
        }),
      );
    },
    caseLogicGradingEffectNodes() {
      const { caseLogicDescription } = this.caseConf;
      if (!caseLogicDescription) {
        return [];
      }
      const isGradingEffectNode = node =>
        node.name === 'Effect' && get(node, 'data.effect.type') === 'GRADING';
      const gradingEffectNodes = toArray(caseLogicDescription.nodes).filter(
        isGradingEffectNode,
      );
      const customEffectChainChildNodes = flatten(
        toArray(caseLogicDescription.nodes)
          .filter(node => node.name === 'EffectChain')
          .filter(
            node =>
              node.data.effectChain &&
              node.data.effectChain.type === 'CUSTOM_EFFECT_CHAIN',
          )
          .map(effectChainNode =>
            toArray(effectChainNode.data.effectChain.nodes).map(
              effectChainChildNode => ({
                ...effectChainChildNode,
                parentNode: effectChainNode,
              }),
            ),
          ),
      );
      const customEffectChainsGradingNodes = customEffectChainChildNodes.filter(
        isGradingEffectNode,
      );
      return gradingEffectNodes.concat(customEffectChainsGradingNodes);
    },
    caseMoulages() {
      return merge({}, this.caseConf.moulages, this.caseConf.structuralMoulages);
    },
    interventionGradingItems() {
      const caseIntvs = this.caseConf.availableInterventions || [];
      const gradingItems = [];
      for (const caseIntv of caseIntvs) {
        const intv = interventions.find(i => i.key === caseIntv.key);
        for (const location of intv.availableLocations) {
          const locationLabel = physicalAssessmentLocations.find(pal => pal.key === location).label.en;
          gradingItems.push({
            id: 'INTERVENTION',
            label: categoryLabel[intv.category].en,
            description: `${locationLabel} - ${intv.label.en}`,
            component: 'INTERVENTION',
            componentKey: intv.key,
            subComponentKey: location,
          });
        }
      }
      return gradingItems;
    },
    totalPoints() {
      return (
        this.dialoguePoints +
        this.customMenuPoints +
        this.assessmentsPoints +
        this.ivPoints +
        this.medicationsPoints +
        this.fluidsPoints +
        this.oxygenRoutesPoints +
        this.proceduresPoints +
        this.monitorActionsPoints +
        this.gradingEffectNodesPoints +
        this.interventionPoints
      );
    },
    dialoguePoints() {
      const { grading } = this.state.caseConf;
      const { questions, commands, calls } = this.caseConf.dialogue;
      if (!grading) {
        return 0;
      }
      const getDialogueItemsPoints = (dialogueKey, dialogueItems) => (dialogueItems || []).reduce(
        (sum, dialogueItem) => sum + this.getPoints(dialogueKey, dialogueItem.id),
        0,
      )
      let points = sum((calls || []).map(call => call.items.reduce((sum, item) => sum + this.getPoints('calls', call.id, 'item', item.id), 0)));
      if (!this.isAiDialogueEnabled) {
        points += getDialogueItemsPoints('questions', questions);
        points += getDialogueItemsPoints('commands', commands);
      }
      return points;
    },
    customMenuPoints() {
      const { grading } = this.state.caseConf;
      const { customMenus } = this.caseConf;
      if (!grading) {
        return 0;
      }
      let points = 0;
      for (const grandparent of customMenus || []) {
        for (const parent of grandparent.items || []) {
          points += this.getPoints('customMenus', grandparent.category, parent.id, 'no-child');
          for (const child of parent.children) {
            points += this.getPoints('customMenus', grandparent.category, parent.id, child.id);
          }
        }
      }
      return points;
    },
    assessmentsPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.assessmentGradingItems.reduce(
        (sum, assessmentItem) =>
          sum +
          this.getPoints(
            'assessments',
            assessmentItem.id,
            assessmentItem.component,
            assessmentItem.componentKey,
          ),
        0,
      );
    },
    ivPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.ivGradingItems.reduce(
        (sum, ivItem) =>
          sum +
          this.getPoints(
            'treatments',
            'IV',
            ivItem.component,
            ivItem.componentKey,
          ),
        0,
      );
    },
    medicationsPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.medicationGradingItems.reduce(
        (sum, medicationItem) =>
          sum +
          this.getPoints(
            'medications',
            medicationItem.id,
            medicationItem.component,
            medicationItem.componentKey,
            medicationItem.subComponentKey,
          ),
        0,
      );
    },
    fluidsPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.fluidGradingItems.reduce(
        (sum, fluidItem) =>
          sum +
          this.getPoints(
            'fluids',
            fluidItem.id,
            fluidItem.component,
            fluidItem.componentKey,
            fluidItem.subComponentKey,
          ),
        0,
      );
    },
    oxygenRoutesPoints() {
      return this.oxygenRoutes.reduce(
        (sum, oxygenRoute) =>
          sum + this.getPoints('oxygenRoutes', oxygenRoute.id),
        0,
      );
    },
    proceduresPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.procedureGradingItems.reduce(
        (sum, procedureItem) =>
          sum +
          this.getPoints(
            'treatments',
            procedureItem.id,
            procedureItem.component,
            procedureItem.componentKey,
          ),
        0,
      );
    },
    monitorActionsPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.monitorActionsGradingItems.reduce(
        (sum, monitorItem) =>
          sum +
          this.getPoints(
            'treatments',
            monitorItem.id,
            monitorItem.component,
            monitorItem.componentKey,
          ),
        0,
      );
    },
    interventionPoints() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.interventionGradingItems.reduce(
        (sum, interventionItem) =>
          sum +
          this.getPoints(
            'interventions',
            interventionItem.id,
            interventionItem.component,
            interventionItem.componentKey,
            interventionItem.subComponentKey,
          ),
        0,
      );
    },
    gradingEffectNodesPoints() {
      return this.caseLogicGradingEffectNodes.reduce(
        (sum, gradingEffectNode) => sum + gradingEffectNode.data.effect.points,
        0,
      );
    },
    dialogueIsCritical() {
      const { grading } = this.state.caseConf;
      const { questions, commands, calls } = this.caseConf.dialogue;
      if (!grading) {
        return 0;
      }
      const getDialogueItemsIsCritical = (dialogueKey, dialogueItems) => (dialogueItems || []).some(
        (dialogueItem) => this.getIsCritical(dialogueKey, dialogueItem.id),
        false,
      );
      let isCritical = (calls || []).some(call => call.items.some((item) => this.getIsCritical('calls', call.id, 'item', item.id)));
      if (!this.isAiDialogueEnabled) {
        isCritical |= getDialogueItemsIsCritical('questions', questions);
        isCritical |= getDialogueItemsIsCritical('commands', commands);
      }
      return isCritical;
    },
    customMenuIsCritical() {
      const { grading } = this.state.caseConf;
      const { customMenus } = this.caseConf;
      if (!grading) {
        return false;
      }
      // Return true if any grade is critical
      for (const grandparent of customMenus || []) {
        for (const parent of grandparent.items || []) {
          if (this.getIsCritical('customMenus', grandparent.category, parent.id, 'no-child')) {
            return true;
          }
          for (const child of parent.children) {
            if (this.getIsCritical('customMenus', grandparent.category, parent.id, child.id)) {
              return true;
            }
          }
        }
      }
      return false;
    },
    assessmentsIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.assessmentGradingItems.some(
        assessmentItem =>
          this.getIsCritical(
            'assessments',
            assessmentItem.id,
            assessmentItem.component,
            assessmentItem.componentKey,
          ),
        0,
      );
    },
    ivIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.ivGradingItems.some(
        ivItem =>
          this.getIsCritical(
            'treatments',
            'IV',
            ivItem.component,
            ivItem.componentKey,
          ),
        0,
      );
    },
    medicationsIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.medicationGradingItems.some(
        medicationItem =>
          this.getIsCritical(
            'medications',
            medicationItem.id,
            medicationItem.component,
            medicationItem.componentKey,
            medicationItem.subComponentKey,
          ),
        0,
      );
    },
    oxygenRoutesIsCritical() {
      return this.oxygenRoutes.some(
        oxygenRoute => this.getIsCritical('oxygenRoutes', oxygenRoute.id),
        0,
      );
    },
    proceduresIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.procedureGradingItems.some(
        procedureItem =>
          this.getIsCritical(
            'treatments',
            procedureItem.id,
            procedureItem.component,
            procedureItem.componentKey,
          ),
        0,
      );
    },
    monitorActionsIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.monitorActionsGradingItems.some(
        monitorItem =>
          this.getIsCritical(
            'treatments',
            monitorItem.id,
            monitorItem.component,
            monitorItem.componentKey,
          ),
        0,
      );
    },
    interventionsIsCritical() {
      const { grading } = this.state.caseConf;
      if (!grading) {
        return 0;
      }
      return this.interventionGradingItems.some(
        interventionItem =>
          this.getIsCritical(
            'interventions',
            interventionItem.id,
            interventionItem.component,
            interventionItem.componentKey,
            interventionItem.subComponentKey,
          ),
        0,
      );
    },
    gradingEffectNodesIsCritical() {
      return this.caseLogicGradingEffectNodes.some(
        gradingEffectNode => gradingEffectNode.data.effect.isCritical,
      );
    },
    outputPreference() {
      return get(this.state.caseConf, 'grading.outputPreference') || 'ACTION_SCORES';
    },
    isAiAvailable() {
      return this.user['https://vrpatients.com/aiActive'];
    },
    isAiDialogueEnabled() {
      return get(this.caseConf, 'dialogue.aiEnabled');
    },
    isAiAssessmentEnabled() {
      return this.state.caseConf.grading && this.state.caseConf.grading.aiAssessmentEnabled;
    },
    promptMarkup() {
      return get(this.state.caseConf, 'grading.aiAssessmentPrompt.markup.en');
    },
    promptContent() {
      return get(this.state.caseConf, 'grading.aiAssessmentPrompt.content.en');
    },
  },
  created() {
    this.state.caseConf = {
      ...this.state.caseConf,
      ...cloneDeep(this.caseConf),
    };
  },
  methods: {
    isActivePanel(panel) {
      return this.state.activeAccordionPanel === panel;
    },
    toggleActivePanel(panel) {
      if (this.isActivePanel(panel)) {
        this.state.activeAccordionPanel = null;
      } else {
        this.state.activeAccordionPanel = panel;
      }
    },
    getGrading(type, id, component, componentKey, subComponentKey) {
      const { grading } = this.state.caseConf;
      if (!grading || !grading[type]) {
        return defaultGrading;
      }
      const typeGradingItems = grading[type];
      const gradingItem = typeGradingItems.find(
        item =>
          item.id === id &&
          (!component || item.component === component) &&
          // Some componentKeys (such as needleGauge) are numbers
          // but in grading they are saved as strings
          (!componentKey ||
            toString(item.componentKey) === toString(componentKey)) &&
          (!subComponentKey ||
            toString(item.subComponentKey) === toString(subComponentKey)),
      );
      if (!gradingItem) {
        return defaultGrading;
      }
      return gradingItem;
    },
    getGrade(type, id, component, componentKey, subComponentKey) {
      return this.getGrading(type, id, component, componentKey, subComponentKey).grade;
    },
    getPoints(type, id, component, componentKey, subComponentKey) {
      return this.getGrading(type, id, component, componentKey, subComponentKey).points;
    },
    getIsCritical(type, id, component, componentKey, subComponentKey) {
      return this.getGrading(type, id, component, componentKey, subComponentKey).isCritical;
    },
    setGrading(type, id, component, componentKey, subComponentKey, gradingKey, value) {
      const { caseConf } = this.state;
      if (!caseConf.grading) {
        this.$set(caseConf, 'grading', {});
      }
      const { grading } = caseConf;
      if (!grading[type]) {
        this.$set(grading, type, []);
      }
      const typeGradingItems = grading[type];
      const gradingItem = typeGradingItems.find(
        item =>
          item.id === id &&
          (!component || item.component === component) &&
          // Some componentKeys (such as needleGauge) are numbers
          // but in grading they are saved as strings
          (!componentKey ||
            toString(item.componentKey) === toString(componentKey)) &&
          (!subComponentKey ||
            toString(item.subComponentKey) === toString(subComponentKey)),
      );
      if (!gradingItem) {
        typeGradingItems.push({
          ...defaultGrading,
          id,
          component,
          componentKey,
          subComponentKey,
          [gradingKey]: value,
        });
      } else {
        gradingItem[gradingKey] = value;
      }
      this.emitUpdate();
    },
    setGrade(type, id, value, component, componentKey, subComponentKey) {
      this.setGrading(type, id, component, componentKey, subComponentKey, 'grade', value);
    },
    setPoints(type, id, value, component, componentKey, subComponentKey) {
      return this.setGrading(
        type,
        id,
        component,
        componentKey,
        subComponentKey,
        'points',
        value,
      );
    },
    setIsCritical(type, id, value, component, componentKey, subComponentKey) {
      return this.setGrading(
        type,
        id,
        component,
        componentKey,
        subComponentKey,
        'isCritical',
        value,
      );
    },
    updateNodeDataInCld(
      nodeToUpdate,
      updates,
      caseLogicDescription = this.caseConf.caseLogicDescription,
    ) {
      const updatedCaseLogicDescription = {
        ...caseLogicDescription,
        nodes: mapValues(caseLogicDescription.nodes, node => {
          if (node.id !== nodeToUpdate.id) {
            return node;
          }
          return merge({}, node, { data: updates });
        }),
      };
      return updatedCaseLogicDescription;
    },
    goToCaseLogicNode(node) {
      if (node.parentNode) {
        this.goToCaseLogicNode(node.parentNode);
        return;
      }
      this.$emit('navigate', 'caseLogic', { nodeId: node.id });
    },
    formatPercentage(percentage) {
      if (!isNumber(percentage) || isNaN(percentage)) {
        return '0%';
      }
      return `${parseInt(percentage * 100, 10)}%`;
    },
    setOutputPreference(value) {
      const { caseConf } = this.state;
      if (!caseConf.grading) {
        this.$set(caseConf, 'grading', {});
      }
      this.$set(caseConf.grading, 'outputPreference', value);
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', { grading: this.state.caseConf.grading });
    },
    oxygenName(id) {
      const oxygen = this.oxygens.find(o => o.id === id);
      return oxygen ? oxygen.name.en : id;
    },
    setAiEnabled(value) {
      const { caseConf } = this.state;
      if (!caseConf.grading) {
        this.$set(caseConf, 'grading', { aiAssessmentEnabled: false });
      }
      this.state.caseConf.grading.aiAssessmentEnabled = value;
      if (value) {
        this.$analytics.event('Authoring Tool', 'ai_grading_enabled', this.user.groupsData[0].name);
      }

      this.emitUpdate();
    },
    editPrompt(value) {
      if (!this.state.caseConf.grading.aiAssessmentPrompt) {
        this.$set(this.state.caseConf.grading, 'aiAssessmentPrompt', {});
      }
      this.state.caseConf.grading.aiAssessmentPrompt = {
        markup: {
          en: value.markup
        },
        content: {
          en: value.content
        }
      };
      this.emitUpdate();
    },
    replacePrompt(value) {
      this.editPrompt(value);
      // A hacky way to get the HtmlEditor to reload its contents
      this.state.reloadPrompt = true;
      this.$nextTick(() => {
        this.state.reloadPrompt = false;
      });
    }
  },
};
</script>

<style scoped>
.accordion-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}
.accordion-trigger {
  background-color: #fcfcfd !important;
  margin: 0;
  text-align: left;
  height: 40px;
  z-index: 6;
}
.accordion-trigger.open .trigger-icon {
  transform: rotateZ(90deg);
}
.accordion-trigger label {
  vertical-align: middle;
}

.accordion-content {
  background-color: #fcfcfd;
  margin-left: 20px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.25);
  border-radius: 0px 0px 4px 4px;
  margin-top: -10px;
  padding-top: 10px;
  max-height: 300px;
  overflow: auto;
}
.rubric-statistics {
  display: inline-block;
  position: absolute;
  left: 150px;
  top: 3.5px;
}

.rubric-statistic {
  border-style: solid;
  border-width: 2px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  padding: 4px 8px;
  margin-left: 5px !important;
}

.rubric-critical {
  margin-left: 5px !important;
}

.fold-enter-active,
.fold-leave-active {
  transition: max-height 0.5s;
}
.fold-enter, .fold-leave-to /* .fade-leave-active below version 2.1.8 */ {
  max-height: 0;
}

.fold-enter-to,
.fold-leave {
  max-height: 300px;
}

.grading-effect-node {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.grading-effect-node .grading-item {
  flex: 1;
}

.preference-assessment-row {
  padding-top: 10px;
  display: flex;
}

.standards-row {
  display: flex;
  justify-content: flex-end;
}

.output-preference {
  min-width: 300px;
}

.ai-switch-container {
  margin-left: 32px;
  display: flex;
  align-items: center;
  flex: 0.99;
}

.ai-switch-container label {
  font-weight: 500;
  font-size: 20px;
}

.ai-description-container {
  display: flex;
  flex-direction: column;
}

.html-editor {
  height: 200px;
  margin-left: 16px;
  width: 100%;
}

.html-editor >>> label {
  font-size: 20px;
  font-weight: 500;
}

.standard-button {
  margin: 0;
}
</style>

<style>
.case-grading .accordion-trigger .md-ripple {
  justify-content: flex-start;
}
</style>